import { Check, ChevronRight, MapPin, Search, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { LOCATION_STORAGE_KEY, defaultMapLocation, popularCities, } from "./LocationSetupData";
import { DeliveryLocation, LocationSearchResult, ReverseGeocodeResponse, } from "./LocationSetupTypes";
import "./LocationSetupModal.css";
type LocationSetupModalProps = { open: boolean; onConfirm: (location: DeliveryLocation) => void; onClose?: () => void; };
const readSavedLocation = (): DeliveryLocation | null => {
    try {
        const value = localStorage.getItem(LOCATION_STORAGE_KEY); return value ? (JSON.parse(value) as DeliveryLocation) : null;
    } catch { return null; }
};

const buildGoogleMapsUrl = (latitude: number, longitude: number) => `https://www.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
const LocationSetupModal = ({
    open,
    onConfirm,
    onClose,
}: LocationSetupModalProps) => {
    const [selectedLocation, setSelectedLocation] = useState<DeliveryLocation | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const [searchResults, setSearchResults] = useState<LocationSearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isDetecting, setIsDetecting] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => {
        if (!open) { return; }
        const savedLocation = readSavedLocation();
        if (savedLocation) { setSelectedLocation({ ...savedLocation, source: "saved", }); }
    }, [open]);
    useEffect(() => {
        if (!open || !onClose) { return; }
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") { onClose(); }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);
    const mapLocation = useMemo(() => ({ latitude: selectedLocation?.latitude ?? defaultMapLocation.latitude, longitude: selectedLocation?.longitude ?? defaultMapLocation.longitude, }), [selectedLocation]);
    const reverseGeocode = async (
        latitude: number,
        longitude: number
    ) => {
        const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
            { headers: { "Accept-Language": "en", }, }
        );
        if (!response.ok) {
            throw new Error("Unable to read the address for this location.");
        }
        return (await response.json()) as ReverseGeocodeResponse;
    };
    const detectCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError("Current location is not supported by this browser.");
            return;
        }
        setError("");
        setSearchResults([]);
        setIsDetecting(true);
        navigator.geolocation.getCurrentPosition(
            async ({ coords }) => {
                try {
                    const result = await reverseGeocode(coords.latitude, coords.longitude);
                    const address = result.address;
                    const label = address?.suburb || address?.city || address?.town || address?.village || "Current Location";
                    setSelectedLocation({
                        label,
                        address: result.display_name || `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`,
                        latitude: coords.latitude,
                        longitude: coords.longitude,
                        source: "current",
                    });
                } catch {
                    setSelectedLocation({
                        label: "Current Location",
                        address: `${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`,
                        latitude: coords.latitude,
                        longitude: coords.longitude,
                        source: "current",
                    });
                } finally {
                    setIsDetecting(false);
                }
            },
            (positionError) => {
                const message =
                    positionError.code === positionError.PERMISSION_DENIED
                        ? "Location permission was denied. Allow location access or search manually."
                        : "Unable to detect your current location. Search for your area instead.";
                setError(message); setIsDetecting(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 15000,
                maximumAge: 0,
            }
        );
    };
    const handleLocationSearch = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const query = searchValue.trim();
        if (query.length < 2) {
            setError("Enter at least 2 characters to search for a location.");
            return;
        }
        try {
            setError("");
            setIsSearching(true);
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=in&q=${encodeURIComponent(query)}`, {
                headers: { "Accept-Language": "en", },
            }
            );
            if (!response.ok) {
                throw new Error("Location search failed.");
            }
            const results = (await response.json()) as LocationSearchResult[];
            setSearchResults(results);
            if (results.length === 0) { setError("No matching locations found. Try a nearby area or city."); }
        } catch {
            setError("Unable to search locations right now. Please try again.");
        } finally {
            setIsSearching(false);
        }
    };
    const handleSearchResultSelect = (result: LocationSearchResult) => {
        const label = result.display_name.split(",")[0] || "Selected Location";
        setSelectedLocation({
            label,
            address: result.display_name,
            latitude: Number(result.lat),
            longitude: Number(result.lon),
            source: "search",
        });
        setSearchValue(label);
        setSearchResults([]);
        setError("");
    };
    const handlePopularCitySelect = (cityId: string) => {
        const city = popularCities.find((item) => item.id === cityId);
        if (!city) { return; }
        setSelectedLocation({
            label: city.name,
            address: `${city.name}, ${city.state}, India`,
            latitude: city.latitude,
            longitude: city.longitude,
            source: "popular",
        });
        setSearchValue(city.name);
        setSearchResults([]);
        setError("");
    };
    const handleConfirmLocation = () => {
        if (!selectedLocation) {
            setError("Detect, search or select a location to continue.");
            return;
        }
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(selectedLocation));
        window.dispatchEvent(new CustomEvent("salar-location-updated", { detail: selectedLocation, }));
        onConfirm(selectedLocation);
    };
    if (!open) { return null; }
    return (
        <div className="location-setup-backdrop" role="presentation"
            onClick={(event) => {
                if (onClose && event.target === event.currentTarget) { onClose(); }
            }}>
            <section className="location-setup-modal" role="dialog" aria-modal="true" aria-labelledby="location-setup-title">
                <div className="location-setup-header">
                    <div>
                        <span className="location-setup-kicker"> DELIVERY LOCATION </span>
                        <h2 id="location-setup-title">Where should we deliver?</h2>
                        <p>Choose your current location or search an area before entering Salar.</p>
                    </div>
                    <div className="location-setup-header-actions">
                        <span className="location-setup-header-pin"><MapPin size={22} strokeWidth={1.9} /></span>
                        {onClose && (
                            <button type="button" className="location-setup-close" onClick={onClose}
                                aria-label="Close location setup">
                                <X size={17} strokeWidth={2} />
                            </button>
                        )}
                    </div>
                </div>
                <div className="location-setup-content">
                    <div className="location-setup-left">
                        <form className="location-setup-search" onSubmit={handleLocationSearch}>
                            <Search size={18} strokeWidth={1.9} />
                            <input type="text"
                                value={searchValue}
                                onChange={(event) => { setSearchValue(event.target.value); setSearchResults([]); setError(""); }}
                                placeholder="Search city, area or locality" aria-label="Search location" />
                            {searchValue && (
                                <button type="button" className="location-setup-clear" onClick={() => {
                                    setSearchValue(""); setSearchResults([]);
                                }}
                                    aria-label="Clear location search">
                                    <X size={15} strokeWidth={2} />
                                </button>
                            )}
                            <button type="submit" className="location-setup-search-submit" disabled={isSearching}>
                                {isSearching ? "Searching..." : "Search"}
                            </button>
                        </form>
                        {searchResults.length > 0 && (
                            <div className="location-setup-search-results">
                                {searchResults.map(
                                    (result) => (
                                        <button key={result.place_id} type="button"
                                            onClick={() => handleSearchResultSelect(result)}>
                                            <span className="location-setup-result-icon">
                                                <MapPin size={16} strokeWidth={1.9} />
                                            </span>
                                            <span>
                                                <strong> {result.display_name.split(",")[0]} </strong>
                                                <small> {result.display_name} </small>
                                            </span>
                                            <ChevronRight size={15} strokeWidth={2} />
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                        <button type="button" className="location-setup-detect"
                            onClick={detectCurrentLocation} disabled={isDetecting}>
                            <span className="location-setup-detect-icon">
                                <i className="bi bi-crosshair" />
                            </span>
                            <span>
                                <strong>
                                    {isDetecting ? "Detecting your location..." : "Detect current location"}
                                </strong>
                                <small> Use your device GPS for better delivery accuracy</small>
                            </span>
                            <ChevronRight size={16} strokeWidth={2} />
                        </button>
                        {error && (
                            <div className="location-setup-error">
                                <i className="bi bi-exclamation-circle" />
                                <span>{error}</span>
                            </div>
                        )}
                        <div className="location-setup-popular">
                            <div className="location-setup-section-title">
                                <div>
                                    <strong> Popular cities</strong>
                                    <span> Quick select </span>
                                </div>
                            </div>
                            <div className="location-setup-city-grid">
                                {popularCities.map(
                                    (city) => {
                                        const selected = selectedLocation?.source === "popular" && selectedLocation.label === city.name;
                                        return (
                                            <button key={city.id} type="button" className={selected ? "selected" : ""}
                                                onClick={() => handlePopularCitySelect(city.id)}>
                                                <span className="location-setup-city-icon">
                                                    <i className={city.iconClass} />
                                                </span>
                                                <span>
                                                    <strong> {city.name} </strong>
                                                    <small> {city.state} </small>
                                                </span>
                                                {selected && (
                                                    <span className="location-setup-city-check">
                                                        <Check size={12} strokeWidth={2.5} />
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    }
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="location-setup-right">
                        <div className="location-setup-map">
                            <iframe title="Selected delivery location" src={buildGoogleMapsUrl(mapLocation.latitude, mapLocation.longitude)} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                            {!selectedLocation && (
                                <div className="location-setup-map-hint">
                                    <span> <i className="bi bi-geo-alt-fill" /> </span>
                                    <div>
                                        <strong> Pick a location </strong>
                                        <small> Detect, search or choose a popular city </small>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={`location-setup-selected ${selectedLocation ? "has-location" : ""}`}>
                            <span className="location-setup-selected-icon">
                                <MapPin size={18} strokeWidth={2} />
                            </span>
                            <div>
                                <small> Selected delivery location</small>
                                <strong> {selectedLocation?.label || "No location selected"}</strong>
                                <p> {selectedLocation?.address || "Your exact delivery area will appear here."}</p>
                            </div>
                        </div>
                        <button type="button" className="location-setup-confirm" onClick={handleConfirmLocation}
                            disabled={!selectedLocation}>
                            <MapPin size={17} strokeWidth={2} />
                            <span> Use this location & continue</span>
                        </button>
                        <p className="location-setup-note">
                            Your location is used only to improve delivery availability and address accuracy.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};
export default LocationSetupModal;
